import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type DragEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api } from "../api.ts";
import { date, money } from "../format.ts";
import { canEdit, useMe } from "../session.ts";
import {
  Badge,
  Button,
  Card,
  cx,
  Empty,
  ErrorText,
  Field,
  Input,
  LoadError,
  Loading,
  Notice,
  Page,
  Select,
  Table,
  Td,
} from "../ui.tsx";
import { AiFeedback } from "./feedback.tsx";
import { defaultKeep, type Keep, type Setup, SetupChoices } from "./setup.tsx";

interface DocRow {
  id: string;
  filename: string;
  status: "queued" | "reading" | "ready" | "failed" | "approved";
  error: string | null;
  created_at: string;
  vendor_id: string | null;
  vendor: string | null;
  questions: number;
}

const STATUS: Record<DocRow["status"], [string, "neutral" | "accent" | "ok" | "danger" | "warn"]> =
  {
    queued: ["Waiting to be read", "neutral"],
    reading: ["Reading…", "neutral"],
    ready: ["Ready to review", "accent"],
    failed: ["Couldn't read it", "danger"],
    approved: ["Approved", "ok"],
  };

export function ContractsList() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: me } = useMe();
  const { data, error, isLoading } = useQuery({
    queryKey: ["documents"],
    queryFn: () => api.get<{ documents: DocRow[] }>("/documents"),
    refetchInterval: (q) =>
      q.state.data?.documents.some((d) => d.status === "queued" || d.status === "reading")
        ? 2000
        : false,
  });
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const upload = useMutation({
    mutationFn: (files: File[]) => {
      const form = new FormData();
      for (const f of files) form.append("file", f);
      return api.upload<{ ids: string[] }>("/documents", form);
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      // One contract: straight to it, where it's read and then approved in one click.
      if (r.ids.length === 1) nav(`/contracts/${r.ids[0]}`);
    },
  });
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (e.dataTransfer.files.length) upload.mutate([...e.dataTransfer.files]);
  };

  return (
    <Page
      help="contracts"
      title="Contracts"
      lead="Drop in MSAs, SLA exhibits and order forms. recoup reads every promise you can enforce, with the contract's own words."
    >
      {canEdit(me) && (
        // biome-ignore lint/a11y/noStaticElementInteractions: a drop target; "Choose files" is the keyboard path
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
          className={cx(
            "flex flex-col items-center justify-center gap-3 rounded-control border-2 border-dashed px-6 py-10 text-center",
            over ? "border-accent bg-accent-soft" : "border-line bg-surface",
          )}
        >
          <p className="font-medium">Drop contracts here</p>
          <p className="text-sm text-muted">PDF, Word, text or Markdown, up to 20 MB each.</p>
          <Button variant="primary" busy={upload.isPending} onClick={() => input.current?.click()}>
            Choose files
          </Button>
          <input
            ref={input}
            type="file"
            multiple
            hidden
            accept=".pdf,.docx,.txt,.md"
            onChange={(e) => {
              if (e.target.files?.length) upload.mutate([...e.target.files]);
              e.target.value = "";
            }}
          />
          <ErrorText error={upload.error} />
        </div>
      )}
      {canEdit(me) && <StandardTerms />}
      <Card flush>
        {isLoading ? (
          <Loading />
        ) : (
          <Table
            head={["Document", "Vendor", "Status", "Added"]}
            empty={!data?.documents.length && <Empty title="No contracts yet." />}
          >
            {data?.documents.map((d) => (
              <tr key={d.id} className="hover:bg-surface-2">
                <Td>
                  <Link className="font-medium hover:text-accent" to={`/contracts/${d.id}`}>
                    {d.filename}
                  </Link>
                  {d.error && <div className="text-xs text-danger">{d.error}</div>}
                </Td>
                <Td>
                  {d.vendor && d.vendor_id ? (
                    <Link className="hover:text-accent" to={`/vendors/${d.vendor_id}`}>
                      {d.vendor}
                    </Link>
                  ) : (
                    (d.vendor ?? <span className="text-muted">New vendor</span>)
                  )}
                </Td>
                <Td>
                  <Badge tone={STATUS[d.status][1]}>{STATUS[d.status][0]}</Badge>
                  {d.status === "ready" && d.questions > 0 && (
                    <span className="ml-2 text-xs text-muted">{d.questions} to check</span>
                  )}
                </Td>
                <Td className="whitespace-nowrap">{date(d.created_at)}</Td>
              </tr>
            ))}
          </Table>
        )}
        <ErrorText error={error} />
      </Card>
    </Page>
  );
}

interface Terms {
  service: string;
  commitment: {
    targetPercent: string;
    window: string;
    covered: string[];
    downtime: {
      weights: { partial_outage: number; degraded: number };
      minimumDurationMinutes: number;
    };
    exclusions: {
      scheduledMaintenance: { noticeHours: number; monthlyAllowanceMinutes: number | null } | null;
      forceMajeure: boolean;
    };
  };
  schedule: {
    tiers: { belowPercent: string; inclusive: boolean; creditPercent: string }[];
    periodCapPercent: string | null;
    soleRemedy: boolean;
  };
  fee: { amountCents: number; per: string; currency: string } | null;
  citations: Record<string, string>;
}

interface Extraction {
  vendor: { name: string; domain: string | null };
  documentKind: string;
  terms: Terms[];
  claimProcedure: {
    readings: { anchor: string; days: number; businessDays: boolean }[];
    email: string | null;
  } | null;
  renewal: { date: string | null; noticeDays: number | null } | null;
  questions: {
    topic: string;
    question: string;
    choices: string[];
    defaultChoice: string;
    quote: string | null;
  }[];
  omittedServices: string[];
}

const KIND: Record<string, string> = {
  msa: "Master agreement",
  sla: "Service level agreement",
  order_form: "Order form",
  dpa: "Data processing agreement",
  other: "Document",
};

/** "Snowflake's", "Amazon Web Services'". */
const possessive = (name: string) => (/s$/i.test(name) ? `${name}'` : `${name}'s`);

/** "123457" cents as "1234.57", by digits: a fee never goes through a float. */
const centsToAmount = (cents: string) => {
  const d = cents.replace(/^-/, "").padStart(3, "0");
  return `${d.slice(0, -2)}.${d.slice(-2)}`;
};

/** No contract in hand: start from a vendor's standard public terms, read like a contract. */
function StandardTerms() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data } = useQuery({
    queryKey: ["standard-terms"],
    queryFn: () =>
      api.get<{ vendors: { key: string; name: string; products: string[] }[] }>("/standard-terms"),
  });
  const [key, setKey] = useState("");
  const [products, setProducts] = useState<string[]>([]);
  const vendor = data?.vendors.find((v) => v.key === key);
  const start = useMutation({
    mutationFn: () =>
      api.post<{ vendorId: string; documentIds: string[]; failed: string[] }>("/standard-terms", {
        key,
        products,
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      if (r.documentIds.length === 1 && !r.failed.length) nav(`/contracts/${r.documentIds[0]}`);
      else {
        setKey("");
        setProducts([]);
      }
    },
  });
  if (!data?.vendors.length) return null;
  return (
    <Card
      title="No contract in hand?"
      description="Many vendors publish the terms every customer gets. Pick a vendor and the products you use: recoup reads its standard terms like a contract, for you to check and approve."
    >
      <div className="grid gap-4">
        <div className="max-w-sm">
          <Field label="Vendor">
            {(id) => (
              <Select
                id={id}
                value={key}
                onChange={(e) => {
                  const next = data.vendors.find((v) => v.key === e.target.value);
                  setKey(e.target.value);
                  // One product: chosen for you.
                  setProducts(next?.products.length === 1 ? next.products : []);
                }}
              >
                <option value="">Choose a vendor</option>
                {data.vendors.map((v) => (
                  <option key={v.key} value={v.key}>
                    {v.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        {vendor && vendor.products.length > 1 && (
          <fieldset className="grid gap-2 text-sm">
            <legend className="mb-1 font-medium">Products you use</legend>
            {vendor.products.map((p) => (
              <label key={p} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={products.includes(p)}
                  onChange={() =>
                    setProducts(
                      products.includes(p) ? products.filter((x) => x !== p) : [...products, p],
                    )
                  }
                />
                {p}
              </label>
            ))}
          </fieldset>
        )}
        <div>
          <Button
            variant="primary"
            disabled={!key || !products.length}
            busy={start.isPending}
            onClick={() => start.mutate()}
          >
            Read the standard terms
          </Button>
        </div>
        {start.data && start.data.failed.length > 0 && (
          <Notice tone="danger">
            recoup couldn't fetch the terms for {start.data.failed.join(", ")} just now. Try again
            in a few minutes.
          </Notice>
        )}
        {start.data && start.data.documentIds.length > 1 && (
          <p className="text-sm text-muted">
            recoup is reading {start.data.documentIds.length} sets of terms; they're listed below.
          </p>
        )}
        <ErrorText error={start.error} />
      </div>
    </Card>
  );
}

export function ContractReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const admin = canEdit(me, "admin");
  const { data, error, isLoading } = useQuery({
    queryKey: ["document", id],
    queryFn: () =>
      api.get<{
        document: {
          id: string;
          filename: string;
          status: DocRow["status"];
          error: string | null;
          extraction: Extraction | null;
          vendor_id: string | null;
          vendor: string | null;
          source_url: string | null;
          created_at: string;
        };
        setup: Setup | null;
        bill: {
          product: string;
          proposal: { month: string; cents: string; currency: string } | null;
        } | null;
        accountDetails: { key: string; label: string; hint: string; value: string }[];
      }>(`/documents/${id}`),
    // While it's being read, and while recoup works out how to watch the vendor.
    refetchInterval: (q) =>
      ["queued", "reading"].includes(q.state.data?.document.status ?? "") ||
      (q.state.data?.document.status === "ready" && q.state.data.setup?.status === "working")
        ? 2000
        : false,
  });
  const setup = data?.setup?.status === "ready" ? data.setup : null;
  const [keep, setKeep] = useState<Keep | null>(null);
  const k = keep ?? defaultKeep(setup?.proposal);
  const x = data?.document.extraction;
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [include, setInclude] = useState<Record<string, boolean>>({});
  const [fees, setFees] = useState<Record<string, { amount: string; per: string }>>({});
  const [vendorName, setVendorName] = useState("");
  const [accountRef, setAccountRef] = useState("");
  const [details, setDetails] = useState<Record<string, string> | null>(null);
  const shownDetails =
    details ?? Object.fromEntries((data?.accountDetails ?? []).map((f) => [f.key, f.value]));
  useEffect(() => {
    if (!x) return;
    setAnswers(Object.fromEntries(x.questions.map((q) => [q.topic, q.defaultChoice])));
    setInclude(Object.fromEntries(x.terms.map((t) => [t.service, true])));
    setVendorName(x.vendor.name);
  }, [x]);

  const approve = useMutation({
    mutationFn: () =>
      api.post<{ vendorId: string }>(`/documents/${id}/approve`, {
        vendorId: data?.document.vendor_id ?? null,
        vendorName,
        vendorDomain: x?.vendor.domain ?? null,
        accountReference: accountRef || null,
        accountDetails: shownDetails,
        answers: Object.entries(answers).map(([topic, answer]) => ({ topic, answer })),
        services: x!.terms.map((t) => {
          const f = fees[t.service];
          const fee =
            f && /^\d[\d,]*(\.\d{1,2})?$/.test(f.amount)
              ? {
                  amountCents: Math.round(Number(f.amount.replace(/,/g, "")) * 100),
                  per: f.per,
                  currency: me?.org?.currency ?? "USD",
                }
              : undefined;
          return {
            service: t.service,
            include: include[t.service] ?? true,
            ...(fee ? { fee } : {}),
          };
        }),
        // One action: what the person kept of the setup starts with the approval.
        ...(setup ? { start: k } : {}),
      }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      nav(`/vendors/${r.vendorId}`);
    },
  });

  if (isLoading) return <Loading />;
  if (error || !data)
    return (
      <Page help="contracts" title="Contract" back={{ to: "/contracts", label: "Contracts" }}>
        <LoadError what="contract" error={error} back={{ to: "/contracts", label: "Contracts" }} />
      </Page>
    );
  const d = data.document;

  if (d.status === "queued" || d.status === "reading")
    return (
      <Page help="contracts" title={d.filename} back={{ to: "/contracts", label: "Contracts" }}>
        <Notice tone="accent">
          recoup is reading this document. It usually takes under a minute, and this page updates
          when it's done. You can leave: we'll email you when it's ready to approve.
        </Notice>
      </Page>
    );
  if (d.status === "failed" || !x)
    return (
      <Page help="contracts" title={d.filename} back={{ to: "/contracts", label: "Contracts" }}>
        <Notice tone="danger">{d.error ?? "recoup couldn't read this document."}</Notice>
        {canEdit(me) && (
          <Button
            onClick={() =>
              api
                .post(`/documents/${id}/retry`)
                .then(() => qc.invalidateQueries({ queryKey: ["document", id] }))
            }
          >
            Read it again
          </Button>
        )}
      </Page>
    );

  const done = d.status === "approved";
  // The last closed month on the company's connected bill, offered as the fee to confirm.
  const bill = data.bill?.proposal ?? null;
  const billMonth = bill
    ? new Date(`${bill.month}-01T00:00:00Z`).toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      })
    : "";
  return (
    <Page
      help="contracts"
      title={`${x.vendor.name}: ${KIND[x.documentKind] ?? "Contract"}`}
      lead={
        done
          ? "Approved: recoup now holds the vendor to these promises."
          : "Check what recoup read, settle the open questions, and approve. Nothing counts until you do."
      }
      back={{ to: "/contracts", label: "Contracts" }}
    >
      {bill && x.terms.length > 1 && !done && (
        <Notice tone="accent">
          Your AWS bill for {data.bill!.product} in {billMonth} was{" "}
          {money(bill.cents, bill.currency)}. Split it across the services below as the fees, if
          that's how you're charged.
        </Notice>
      )}
      {d.source_url && (
        <Notice tone="accent">
          These are {possessive(d.vendor ?? x.vendor.name)} standard terms, from{" "}
          <a className="break-all underline" href={d.source_url} target="_blank" rel="noreferrer">
            its own site
          </a>{" "}
          on {date(d.created_at)}. They apply unless your company signed its own agreement with{" "}
          {d.vendor ?? x.vendor.name}; if it did, upload that instead.
        </Notice>
      )}
      {x.questions.length > 0 && !done && (
        <Card
          title="A few things to confirm"
          description="recoup picked the reading that pays you least, so nothing is overstated. Change any you know better."
        >
          <div className="flex flex-col gap-5">
            {x.questions.map((q) => (
              <fieldset key={q.topic} className="flex flex-col gap-2">
                <legend className="text-sm font-medium">{q.topic}</legend>
                <p className="text-sm">{q.question}</p>
                {q.quote && (
                  <blockquote className="border-l-2 border-line pl-3 text-sm text-muted">
                    “{q.quote}”
                  </blockquote>
                )}
                <div className="flex flex-wrap gap-2">
                  {q.choices.map((ch) => (
                    <label
                      key={ch}
                      className={cx(
                        "cursor-pointer rounded-chip border px-3 py-1.5 text-sm",
                        answers[q.topic] === ch ? "border-accent bg-accent-soft" : "border-line",
                      )}
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        name={q.topic}
                        checked={answers[q.topic] === ch}
                        onChange={() => setAnswers({ ...answers, [q.topic]: ch })}
                      />
                      {ch}
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
        </Card>
      )}

      {x.terms.length === 0 ? (
        <Notice>
          recoup found no availability promise it could trace to this document's words. If it's in
          another document, such as an SLA exhibit, upload that too.
        </Notice>
      ) : (
        x.terms.map((t) => (
          <Card
            key={t.service}
            title={t.service}
            description={`${t.commitment.targetPercent}% uptime each ${t.commitment.window}${t.commitment.covered.length ? `, covering ${t.commitment.covered.join(", ")}` : ""}`}
            actions={
              !done && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={include[t.service] ?? true}
                    onChange={(e) => setInclude({ ...include, [t.service]: e.target.checked })}
                  />{" "}
                  You buy this service
                </label>
              )
            }
          >
            <dl className="grid gap-4 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted">Counts as downtime</dt>
                <dd>
                  Full outages
                  {t.commitment.downtime.weights.partial_outage ? ", partial outages" : ""}
                  {t.commitment.downtime.weights.degraded ? ", degraded performance" : ""}
                  {t.commitment.downtime.minimumDurationMinutes
                    ? `; ${t.commitment.downtime.minimumDurationMinutes} minutes or longer`
                    : ""}
                </dd>
              </div>
              <div>
                <dt className="text-muted">Excluded</dt>
                <dd>
                  {[
                    t.commitment.exclusions.scheduledMaintenance &&
                      `maintenance with ${t.commitment.exclusions.scheduledMaintenance.noticeHours} hours' notice`,
                    t.commitment.exclusions.forceMajeure && "force majeure",
                  ]
                    .filter(Boolean)
                    .join(", ") || "Nothing"}
                </dd>
              </div>
              <div>
                <dt className="text-muted">Credits</dt>
                <dd className="tabular">
                  {t.schedule.tiers.length === 0
                    ? "No credit table found"
                    : t.schedule.tiers.map((tier) => (
                        <div key={`${tier.belowPercent}:${tier.creditPercent}`}>
                          {tier.inclusive ? "At or below" : "Below"} {tier.belowPercent}%:{" "}
                          {tier.creditPercent}% of the fee
                        </div>
                      ))}
                  {t.schedule.periodCapPercent && (
                    <div className="text-muted">At most {t.schedule.periodCapPercent}% a month</div>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted">Fee</dt>
                <dd>
                  {t.fee ? (
                    `${(t.fee.amountCents / 100).toLocaleString(undefined, { style: "currency", currency: t.fee.currency })} per ${t.fee.per}`
                  ) : done ? (
                    "Not in this document"
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        aria-label="Fee"
                        inputMode="decimal"
                        placeholder="Amount"
                        className="w-32"
                        value={fees[t.service]?.amount ?? ""}
                        onChange={(e) =>
                          setFees({
                            ...fees,
                            [t.service]: {
                              per: fees[t.service]?.per ?? "month",
                              amount: e.target.value,
                            },
                          })
                        }
                      />
                      <Select
                        aria-label="Per"
                        className="w-32"
                        value={fees[t.service]?.per ?? "month"}
                        onChange={(e) =>
                          setFees({
                            ...fees,
                            [t.service]: {
                              amount: fees[t.service]?.amount ?? "",
                              per: e.target.value,
                            },
                          })
                        }
                      >
                        <option value="month">per month</option>
                        <option value="quarter">per quarter</option>
                        <option value="year">per year</option>
                      </Select>
                    </div>
                  )}
                  {!t.fee && !done && bill && x.terms.length === 1 && (
                    <p className="mt-2 text-muted">
                      Your bill for {data.bill!.product} in {billMonth}:{" "}
                      {money(bill.cents, bill.currency)}.{" "}
                      <button
                        type="button"
                        className="font-medium text-accent hover:underline"
                        onClick={() =>
                          setFees({
                            ...fees,
                            [t.service]: {
                              amount: centsToAmount(bill.cents),
                              per: "month",
                            },
                          })
                        }
                      >
                        Use it
                      </button>
                    </p>
                  )}
                </dd>
              </div>
            </dl>
            <details className="mt-4 text-sm">
              <summary className="cursor-pointer text-muted">The contract's words</summary>
              <dl className="mt-2 flex flex-col gap-2">
                {Object.entries(t.citations).map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-xs text-muted capitalize">{k}</dt>
                    <dd className="whitespace-pre-wrap">“{v}”</dd>
                  </div>
                ))}
              </dl>
            </details>
          </Card>
        ))
      )}

      {x.omittedServices.length > 0 && (
        <Notice tone="accent">
          Also named but not bought, so not measured: {x.omittedServices.join(", ")}.
        </Notice>
      )}

      <Card title="Claims and renewal">
        <dl className="grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted">Claim window</dt>
            <dd>
              {x.claimProcedure
                ? x.claimProcedure.readings
                    .map(
                      (r) =>
                        `${r.days} ${r.businessDays ? "business " : ""}days after ${r.anchor === "month_end" ? "the month ends" : "the incident"}`,
                    )
                    .join(" or ")
                : "Not stated; recoup uses 15 days after the month ends"}
            </dd>
          </div>
          <div>
            <dt className="text-muted">Where claims go</dt>
            <dd>{x.claimProcedure?.email ?? "Not stated"}</dd>
          </div>
          <div>
            <dt className="text-muted">Renewal notice</dt>
            <dd>{x.renewal?.noticeDays ? `${x.renewal.noticeDays} days` : "Not stated"}</dd>
          </div>
        </dl>
      </Card>

      <AiFeedback task="contract" refId={d.id} question="Did recoup read this contract right?" />

      {!done && admin && (
        <Card
          title={setup ? `Approve and start monitoring ${vendorName || "this vendor"}` : "Approve"}
          description={
            setup
              ? "Here's how recoup will monitor them. Untick anything that's wrong; recoup then measures the last 12 months and opens a claim for every month that owes you a credit."
              : "recoup then works out how to monitor the vendor, measures the last 12 months, and opens a claim for every month that owes you a credit."
          }
        >
          {setup?.proposal && (
            <div className="mb-4">
              <SetupChoices
                p={setup.proposal}
                vendor={vendorName || "the vendor"}
                keep={k}
                onChange={setKeep}
              />
            </div>
          )}
          {data?.setup?.status === "working" && (
            <p className="mb-4 text-sm text-muted">
              recoup is working out how to monitor this vendor… You can approve now and start
              monitoring from the vendor's page.
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            {d.vendor_id ? (
              <p className="text-sm">
                Adds these terms to{" "}
                <Link className="text-accent" to={`/vendors/${d.vendor_id}`}>
                  {d.vendor}
                </Link>
                .
              </p>
            ) : (
              <Field label="Vendor name">
                {(fid) => (
                  <Input
                    id={fid}
                    value={vendorName}
                    onChange={(e) => setVendorName(e.target.value)}
                  />
                )}
              </Field>
            )}
            <Field label="Your account number with them" hint="Optional; it goes on claim letters.">
              {(fid) => (
                <Input
                  id={fid}
                  value={accountRef}
                  onChange={(e) => setAccountRef(e.target.value)}
                />
              )}
            </Field>
            {/* What the vendor's claim form needs, so nothing is left to do after approving. */}
            {(data?.accountDetails ?? []).map((f) => (
              <Field key={f.key} label={f.label} hint={`Optional. ${f.hint}`}>
                {(fid) => (
                  <Input
                    id={fid}
                    value={shownDetails[f.key] ?? ""}
                    onChange={(e) => setDetails({ ...shownDetails, [f.key]: e.target.value })}
                  />
                )}
              </Field>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              busy={approve.isPending}
              disabled={!vendorName || x.terms.length === 0}
              onClick={() => approve.mutate()}
            >
              {x.questions.length
                ? setup
                  ? "Approve all and start monitoring"
                  : "Approve all with these readings"
                : setup
                  ? "Approve and start monitoring"
                  : "Approve"}
            </Button>
            <ErrorText error={approve.error} />
          </div>
        </Card>
      )}
      {!done && !admin && <Notice tone="accent">An owner or admin approves terms.</Notice>}
    </Page>
  );
}
